import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  applyAffiliateRedemption,
  getAffiliateRedemption,
  rejectAffiliateRedemption,
  type AffiliateRedemptionRow,
} from "../api/affiliatePayouts";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";

// Affiliate-redemption detail.
//
// Two operator mutations (both operator-records only — no on-chain
// effect, no cross-service API call from this browser bundle):
//
//   1. Apply  — POST /redemptions/:id/apply. Confirms the operator
//               recorded that the credit was honored for the
//               selected Gao ecosystem target. Downstream services
//               handle the actual delivery; that is OUT of scope
//               for Admin Web.
//   2. Reject — POST /redemptions/:id/reject with a reason string.
//
// The confirm modal copy for Apply matches the operator brief
// verbatim: "This records that the credit was honored for the
// selected Gao ecosystem target."

type Dialog = { kind: "none" } | { kind: "apply" } | { kind: "reject" };

const APPLY_COPY =
  "This records that the credit was honored for the selected Gao ecosystem target.";

function AffiliateRedemptionDetailInner({ id }: { id: string }) {
  const [detail, setDetail] = useState<AffiliateRedemptionRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [reasonInput, setReasonInput] = useState("");
  const [dialog, setDialog] = useState<Dialog>({ kind: "none" });
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Post-mutation banners. `successNotice` confirms the BE accepted
  // the action; `refetchWarning` is set when the follow-up GET to
  // /:id failed AFTER the mutation succeeded — the operator needs
  // to reload to see the authoritative current state.
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [refetchWarning, setRefetchWarning] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const r = await getAffiliateRedemption(id);
      setDetail(r);
    } catch (e) {
      setLoadError(mapAffiliateError(e).message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function refetchDetail(): Promise<boolean> {
    try {
      const r = await getAffiliateRedemption(id);
      setDetail(r);
      return true;
    } catch {
      return false;
    }
  }

  function openDialog(d: Dialog) {
    setDialogError(null);
    setDialog(d);
  }
  function closeDialog() {
    if (busy) return;
    setDialog({ kind: "none" });
    setDialogError(null);
  }

  async function performMutation(
    successLabel: string,
    mutate: () => Promise<unknown>,
  ): Promise<void> {
    setBusy(true);
    setDialogError(null);
    try {
      await mutate();
    } catch (e) {
      setDialogError(mapAffiliateError(e).message);
      setBusy(false);
      return;
    }
    setDialog({ kind: "none" });
    setSuccessNotice(successLabel);
    setRefetchWarning(null);
    const refetched = await refetchDetail();
    if (!refetched) {
      setRefetchWarning(
        `${successLabel} The current row state could not be refreshed — please reload the page to see authoritative state.`,
      );
    }
    setBusy(false);
  }

  async function runApply() {
    if (!detail) return;
    await performMutation("Redemption applied.", () =>
      applyAffiliateRedemption(detail.id),
    );
  }

  async function runReject() {
    if (!detail) return;
    await performMutation("Redemption rejected.", () =>
      rejectAffiliateRedemption(detail.id, {
        reason: reasonInput.trim(),
      }),
    );
  }

  if (loading && !detail) {
    return <p style={{ padding: 12, color: "var(--fg-dim)" }}>Loading…</p>;
  }
  if (loadError && !detail) {
    return (
      <div style={{ padding: 12 }}>
        <ErrorBox text={loadError} />
        <p style={{ marginTop: 12 }}>
          <Link to="/admin/affiliate/redemptions" style={{ color: "var(--accent, #2c7be5)" }}>
            ← Back to queue
          </Link>
        </p>
      </div>
    );
  }
  if (!detail) return null;

  const reasonValid = reasonInput.trim().length > 0;
  const canAct = detail.status === "queued";

  return (
    <div>
      <p style={{ margin: "8px 0", fontSize: 12 }}>
        <Link to="/admin/affiliate/redemptions" style={{ color: "var(--accent, #2c7be5)" }}>
          ← All redemptions
        </Link>
      </p>

      <div
        role="note"
        style={{
          margin: "8px 0",
          padding: "10px 12px",
          border: "1px solid var(--ok, #0a9444)",
          background: "rgba(10, 148, 68, 0.06)",
          color: "var(--ok, #0a9444)",
          borderRadius: 4,
          fontSize: 12.5,
          fontWeight: 600,
        }}
      >
        Credit redemption — applies in-ecosystem CREDITS, not Partner Cash. No
        on-chain transaction and no proposal: operator-records only (apply /
        reject).
      </div>

      {successNotice && (
        <div
          role="status"
          style={{
            padding: 10,
            border: "1px solid var(--ok, #0a9444)",
            background: "rgba(10, 148, 68, 0.06)",
            color: "var(--ok, #0a9444)",
            borderRadius: 4,
            fontSize: 13,
            margin: "8px 0",
          }}
        >
          ✓ {successNotice}
        </div>
      )}
      {refetchWarning && (
        <div
          role="alert"
          style={{
            padding: 10,
            border: "1px solid var(--warn, #b58900)",
            background: "rgba(251, 191, 36, 0.08)",
            color: "var(--warn, #b58900)",
            borderRadius: 4,
            fontSize: 13,
            margin: "8px 0",
          }}
        >
          {refetchWarning}
        </div>
      )}

      <Section title="Queue row">
        <DetailGrid r={detail} />
      </Section>

      <Section title="Actions">
        {canAct ? (
          <>
            {/* Rejection reason is ALWAYS rendered + editable while the row
                is actionable — never hidden behind the confirm overlay. The
                operator types it first; Reject only enables once non-empty. */}
            <div style={{ marginBottom: 12 }}>
              <label
                htmlFor="redemption-reject-reason"
                style={{ fontSize: 12, color: "var(--fg-dim)", display: "block" }}
              >
                Reason (required, recorded in the BE audit log)
              </label>
              <textarea
                id="redemption-reject-reason"
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                rows={3}
                placeholder="e.g. ineligible target, fraud signal, duplicate of req_…"
                style={{ width: "100%", fontSize: 12, marginTop: 4, padding: 6 }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => openDialog({ kind: "apply" })}
                style={primaryBtnStyle()}
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => openDialog({ kind: "reject" })}
                disabled={!reasonValid}
                title={reasonValid ? "" : "Enter a rejection reason first"}
                style={neutralBtnStyle(reasonValid)}
              >
                Reject
              </button>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 12, color: "var(--fg-dim)" }}>
            Row is in terminal state — view only.
          </p>
        )}
      </Section>

      <ConfirmDialog
        open={dialog.kind === "apply"}
        title="Apply affiliate redemption"
        description={
          <>
            <p style={{ margin: "0 0 6px 0" }}>{APPLY_COPY}</p>
            <p style={{ margin: 0, color: "var(--fg-dim)", fontSize: 12 }}>
              Operator-records only. No on-chain side-effect, no external
              cross-service call from this browser bundle. Downstream services
              handle credit delivery.
            </p>
          </>
        }
        rows={[
          { key: "redemptionId", value: <code>{detail.id}</code> },
          {
            key: "amount",
            value: (
              <>
                {detail.amountHumanReadable ?? detail.amountMinor}{" "}
                <code>{detail.currency}</code>{" "}
                <span style={{ color: "var(--fg-dim)" }}>
                  ({detail.amountMinor} minor)
                </span>
              </>
            ),
          },
          { key: "target", value: <code>{detail.target}</code> },
        ]}
        confirmLabel="Apply"
        tone="normal"
        busy={busy}
        error={dialogError}
        onConfirm={() => void runApply()}
        onCancel={closeDialog}
      />

      <ConfirmDialog
        open={dialog.kind === "reject"}
        title="Reject affiliate redemption"
        description={
          <p style={{ margin: 0 }}>
            Records operator-rejection of this redemption request. The reason
            is persisted in the BE audit log.
          </p>
        }
        rows={[
          { key: "redemptionId", value: <code>{detail.id}</code> },
          {
            key: "amount",
            value: (
              <>
                {detail.amountHumanReadable ?? detail.amountMinor}{" "}
                <code>{detail.currency}</code>
              </>
            ),
          },
          { key: "target", value: <code>{detail.target}</code> },
          {
            key: "reason",
            value: reasonValid ? (
              <code style={{ whiteSpace: "pre-wrap" }}>{reasonInput.trim()}</code>
            ) : (
              <em style={{ color: "var(--danger, #d23f3f)" }}>required</em>
            ),
          },
        ]}
        confirmLabel="Reject"
        tone="warning"
        busy={busy}
        confirmDisabled={!reasonValid}
        error={dialogError}
        onConfirm={() => void runReject()}
        onCancel={closeDialog}
      />
    </div>
  );
}

export function AffiliateRedemptionDetail() {
  const { id } = useParams<{ id: string }>();
  return (
    <div>
      <PageHeader
        title={`Affiliate redemption — ${id ?? ""}`}
        subtitle="Apply · Reject. Operator-records only — no on-chain effect, no external cross-service call from the browser."
        method="GET / POST"
        path="/v2/admin/bff/affiliate/redemptions/:id"
      />
      <AuthRequired>
        {id ? (
          <AffiliateRedemptionDetailInner id={id} />
        ) : (
          <ErrorBox text="Missing :id in URL" />
        )}
      </AuthRequired>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "14px 16px",
        margin: "16px 0",
        background: "var(--bg-card)",
      }}
    >
      <h3 style={{ margin: "0 0 12px 0", fontSize: 14 }}>{title}</h3>
      {children}
    </section>
  );
}

function DetailGrid({ r }: { r: AffiliateRedemptionRow }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <tbody>
        <Row k="id" v={<code>{r.id}</code>} />
        <Row k="identityId" v={<code>{r.identityId}</code>} />
        <Row k="status" v={<strong>{r.status}</strong>} />
        <Row
          k="amount"
          v={
            <>
              {r.amountHumanReadable ?? r.amountMinor}{" "}
              <code>{r.currency}</code>{" "}
              <span style={{ color: "var(--fg-dim)" }}>
                ({r.amountMinor} minor)
              </span>
            </>
          }
        />
        <Row k="target" v={<code>{r.target}</code>} />
        <Row k="requestedAt" v={<code>{r.requestedAt}</code>} />
        <Row k="appliedAt" v={r.appliedAt ? <code>{r.appliedAt}</code> : "—"} />
        <Row k="rejectedAt" v={r.rejectedAt ? <code>{r.rejectedAt}</code> : "—"} />
        <Row
          k="rejectionReason"
          v={
            r.rejectionReason ? (
              <span style={{ whiteSpace: "pre-wrap" }}>{r.rejectionReason}</span>
            ) : (
              "—"
            )
          }
        />
      </tbody>
    </table>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <tr style={{ borderTop: "1px solid var(--border)" }}>
      <td style={{ padding: "6px 8px", color: "var(--fg-dim)", width: 200 }}>{k}</td>
      <td style={{ padding: "6px 8px", wordBreak: "break-all" }}>{v}</td>
    </tr>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        padding: 12,
        border: "1px solid var(--danger, #d23f3f)",
        background: "rgba(210, 63, 63, 0.06)",
        color: "var(--danger, #d23f3f)",
        borderRadius: 4,
        fontSize: 13,
      }}
    >
      {text}
    </div>
  );
}

function primaryBtnStyle() {
  return {
    padding: "6px 16px",
    fontSize: 13,
    background: "var(--accent, #2c7be5)",
    color: "white",
    border: "none",
    borderRadius: 3,
    cursor: "pointer" as const,
    fontWeight: 600,
  };
}

function neutralBtnStyle(enabled = true) {
  return {
    padding: "6px 16px",
    fontSize: 13,
    background: "transparent",
    color: "var(--danger, #d23f3f)",
    border: "1px solid var(--danger, #d23f3f)",
    borderRadius: 3,
    cursor: enabled ? ("pointer" as const) : ("default" as const),
    opacity: enabled ? 1 : 0.5,
    fontWeight: 600,
  };
}
